import { unavailableTmsOperation } from './tmsConnection';
import { reportClientError } from './clientErrorReporter';
import {
  buildPublicRequestHeaders,
  resolvePublicRequestId,
} from './publicRequestReliability';
import { buildReachPublicApiUrl, getReachPublicApiBaseUrl } from './reachPublicApi';

const API_BASE_URL = getReachPublicApiBaseUrl();
const DEFAULT_OWNER_UID = String(
  process.env.REACT_APP_CARRIER_FLOW_OWNER_UID
  || process.env.REACT_APP_PUBLIC_INTAKE_OWNER_UID
  || ''
).trim();
const DEFAULT_WORKSPACE_OWNER_UID = String(
  process.env.REACT_APP_CARRIER_FLOW_WORKSPACE_OWNER_UID
  || process.env.REACT_APP_PUBLIC_INTAKE_WORKSPACE_OWNER_UID
  || ''
).trim();
const PUBLIC_PAYMENT_CONFIG_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_PUBLIC_PAYMENT_CONFIG_PATH
  || '/api/public/payments/config'
).trim();
const API_REQUEST_TIMEOUT_MS = Number(
  process.env.REACT_APP_CARRIER_FLOW_API_TIMEOUT_MS || process.env.REACT_APP_REQUEST_TIMEOUT_MS || 12000
) || 12000;
const PUBLIC_PAYMENT_CONFIG_CACHE_TTL_MS = Math.max(
  0,
  Math.min(Number(process.env.REACT_APP_PUBLIC_PAYMENT_CONFIG_CACHE_TTL_MS || 30000) || 30000, 300000)
);
const PUBLIC_PAYMENT_CONFIG_CACHE_MAX_ENTRIES = Math.max(
  10,
  Math.min(Number(process.env.REACT_APP_PUBLIC_PAYMENT_CONFIG_CACHE_MAX_ENTRIES || 200) || 200, 1000)
);
const PUBLIC_PAYMENT_STATUS_INFLIGHT_MAX_ENTRIES = Math.max(
  10,
  Math.min(Number(process.env.REACT_APP_PUBLIC_PAYMENT_STATUS_INFLIGHT_MAX_ENTRIES || 200) || 200, 1000)
);

const PUBLIC_PAYMENT_FLOWS = new Set(['consultation', 'webinar', 'paperwork_package']);
const PUBLIC_PAYMENT_PROVIDERS = new Set(['manual', 'paypal', 'stripe', 'swift']);
const PUBLIC_PROVIDER_LABELS = {
  manual: 'Manual review',
  paypal: 'PayPal',
  stripe: 'Stripe',
  swift: 'SWIFT transfer',
};
const SENSITIVE_PAYMENT_CONFIG_KEYS = new Set([
  'accountNumber',
  'bankAccount',
  'bankAddress',
  'bankName',
  'beneficiary',
  'beneficiaryAddress',
  'businessIdentity',
  'clientId',
  'clientSecret',
  'ein',
  'iban',
  'merchantEmail',
  'paymentLinkUrl',
  'publishableKey',
  'routingNumber',
  'secret',
  'secretKey',
  'stripeAccountId',
  'swiftCode',
  'webhookSecret',
]);

const publicPaymentConfigCache = new Map();
const publicPaymentConfigInflight = new Map();
const publicPaymentStatusInflight = new Map();

function sanitizeText(value, maxLength = 240) {
  return String(value || '').trim().slice(0, maxLength);
}

function normalizeMoney(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return Number(numeric.toFixed(2));
}

function sanitizeCurrencyCode(value, fallback = 'USD') {
  const normalized = sanitizeText(value, 12).toUpperCase().replace(/[^A-Z]/g, '');
  return /^[A-Z]{3}$/.test(normalized) ? normalized : fallback;
}

export function sanitizePublicPaymentProvider(value, fallback = 'manual') {
  const normalized = sanitizeText(value, 40).toLowerCase();
  return PUBLIC_PAYMENT_PROVIDERS.has(normalized) ? normalized : fallback;
}

function sanitizePublicPaymentFlow(value) {
  const normalized = sanitizeText(value, 40).toLowerCase();
  return PUBLIC_PAYMENT_FLOWS.has(normalized) ? normalized : '';
}

function sanitizePublicApiPath(value, fallback = '') {
  const normalized = sanitizeText(value, 240);
  if (!normalized || !normalized.startsWith('/api/public/')) {
    return fallback;
  }
  return normalized;
}

function sanitizeProviderLabel(provider, value) {
  return sanitizeText(value, 120) || PUBLIC_PROVIDER_LABELS[provider] || provider;
}

function buildApiUrl(path) {
  const normalizedPath = sanitizePublicApiPath(path, '/api/public/payments/config');
  return buildReachPublicApiUrl(normalizedPath);
}

function buildAbsoluteApiUrl(path) {
  const apiUrl = buildApiUrl(path);
  const origin = typeof window !== 'undefined' && window.location?.origin
    ? window.location.origin
    : 'https://magellan.international';
  return new URL(apiUrl, origin);
}

function getOwnerPayload() {
  const ownerUid = DEFAULT_WORKSPACE_OWNER_UID || DEFAULT_OWNER_UID;
  if (!ownerUid) {
    return {};
  }

  return DEFAULT_WORKSPACE_OWNER_UID
    ? { workspaceOwnerUid: DEFAULT_WORKSPACE_OWNER_UID }
    : { ownerUid: DEFAULT_OWNER_UID };
}

function sanitizePaperworkPackageSku(value) {
  const normalized = sanitizeText(value, 40).toLowerCase();
  return ['basic', 'standard', 'premium'].includes(normalized) ? normalized : '';
}

function buildPublicPaymentConfigUrl({ flow, webinarId = '', productSku = '' }) {
  const url = buildAbsoluteApiUrl(PUBLIC_PAYMENT_CONFIG_PATH);
  url.searchParams.set('flow', flow);
  if (webinarId) {
    url.searchParams.set('webinarId', webinarId);
  }
  if (productSku) {
    url.searchParams.set('productSku', productSku);
  }
  Object.entries(getOwnerPayload()).forEach(([key, value]) => {
    const normalizedValue = sanitizeText(value, 180);
    if (normalizedValue) {
      url.searchParams.set(key, normalizedValue);
    }
  });
  return url.toString();
}

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    console.error('Payment config response was not valid JSON:', error);
    throw new Error('The payment configuration service returned an invalid response.');
  }
}

function normalizeProviderOption(option = {}, paymentCurrency = 'USD') {
  if (!option || typeof option !== 'object') {
    return null;
  }

  const provider = sanitizePublicPaymentProvider(option.provider, '');
  if (!provider) {
    return null;
  }

  const supportedCurrencies = Array.isArray(option.supportedCurrencies)
    ? option.supportedCurrencies
      .map((currency) => sanitizeCurrencyCode(currency, ''))
      .filter(Boolean)
    : undefined;
  const settlementWindowDays = Number(option.settlementWindowDays);

  return {
    provider,
    displayName: sanitizeProviderLabel(provider, option.displayName),
    enabled: Boolean(option.enabled),
    ready: Boolean(option.ready),
    unavailableReason: sanitizeText(option.unavailableReason, 80),
    checkoutMode: sanitizeText(option.checkoutMode, 80),
    supportsRedirect: Boolean(option.supportsRedirect),
    requiresOwnerConfirmation: provider === 'swift' ? Boolean(option.requiresOwnerConfirmation) : undefined,
    requireProofUpload: provider === 'swift' ? Boolean(option.requireProofUpload) : undefined,
    settlementWindowDays: provider === 'swift' && Number.isFinite(settlementWindowDays)
      ? Math.max(0, Math.min(settlementWindowDays, 90))
      : undefined,
    supportedCurrencies: provider === 'swift' ? supportedCurrencies || [paymentCurrency] : undefined,
  };
}

function normalizeCheckoutConfig(checkout = {}, { flow, paymentProvider, paymentRequired, productSku = '' }) {
  const provider = sanitizePublicPaymentProvider(checkout?.provider, paymentProvider);
  const base = {
    provider,
    paymentRequired: Boolean(paymentRequired) && provider !== 'manual',
  };

  if (flow === 'paperwork_package' && productSku) {
    const createOrderPath = `/api/public/paperwork-packages/${productSku}/orders`;
    if (provider === 'manual') {
      return {
        ...base,
        paymentRequired: Boolean(paymentRequired),
        createOrderPath: sanitizePublicApiPath(checkout?.createOrderPath, createOrderPath),
      };
    }
    if (provider === 'paypal') {
      return {
        ...base,
        createOrderPath: sanitizePublicApiPath(checkout?.createOrderPath, createOrderPath),
        createProviderOrderPathTemplate: sanitizePublicApiPath(checkout?.createProviderOrderPathTemplate, '/api/public/paperwork-packages/orders/:orderId/paypal/create-order'),
        captureOrderPathTemplate: sanitizePublicApiPath(checkout?.captureOrderPathTemplate, '/api/public/paperwork-packages/orders/:orderId/paypal/capture-order'),
      };
    }
    if (provider === 'stripe') {
      return {
        ...base,
        createOrderPath: sanitizePublicApiPath(checkout?.createOrderPath, createOrderPath),
        createCheckoutSessionPathTemplate: sanitizePublicApiPath(checkout?.createCheckoutSessionPathTemplate, '/api/public/paperwork-packages/orders/:orderId/stripe/create-checkout-session'),
      };
    }
    if (provider === 'swift') {
      return {
        ...base,
        createOrderPath: sanitizePublicApiPath(checkout?.createOrderPath, createOrderPath),
        startPathTemplate: sanitizePublicApiPath(checkout?.startPathTemplate, '/api/public/paperwork-packages/orders/:orderId/swift/start'),
      };
    }
  }

  if (!base.paymentRequired) {
    return base;
  }

  if (provider === 'paypal') {
    const createFallback = flow === 'webinar'
      ? '/api/public/webinars/paypal/create-order'
      : '/api/public/consultations/paypal/create-order';
    const captureFallback = flow === 'webinar'
      ? '/api/public/webinars/paypal/capture-order'
      : '/api/public/consultations/paypal/capture-order';
    return {
      ...base,
      createOrderPath: sanitizePublicApiPath(checkout?.createOrderPath, createFallback),
      captureOrderPath: sanitizePublicApiPath(checkout?.captureOrderPath, captureFallback),
    };
  }

  if (provider === 'stripe') {
    const fallback = flow === 'webinar'
      ? '/api/public/webinars/stripe/create-checkout-session'
      : '/api/public/consultations/stripe/create-checkout-session';
    return {
      ...base,
      createCheckoutSessionPath: sanitizePublicApiPath(checkout?.createCheckoutSessionPath, fallback),
    };
  }

  if (provider === 'swift') {
    const fallback = flow === 'webinar'
      ? '/api/public/webinars/swift/start'
      : '/api/public/consultations/swift/start';
    return {
      ...base,
      startPath: sanitizePublicApiPath(checkout?.startPath, fallback),
    };
  }

  return base;
}

function assertNoSensitivePaymentFields(config = {}) {
  const serializedKeys = [];
  const visit = (value) => {
    if (!value || typeof value !== 'object') return;
    Object.keys(value).forEach((key) => {
      serializedKeys.push(key);
      visit(value[key]);
    });
  };
  visit(config);
  const leakedKey = serializedKeys.find((key) => SENSITIVE_PAYMENT_CONFIG_KEYS.has(key));
  if (leakedKey) {
    throw new Error(`Payment configuration exposed a private field: ${leakedKey}`);
  }
}

export function normalizePublicPaymentConfig(rawConfig = {}, fallback = {}) {
  assertNoSensitivePaymentFields(rawConfig);

  const flow = sanitizePublicPaymentFlow(rawConfig.flow || fallback.flow) || 'consultation';
  const productSku = sanitizePaperworkPackageSku(rawConfig.productSku || fallback.productSku);
  const paymentAmount = normalizeMoney(rawConfig.paymentAmount ?? rawConfig.amount);
  const paymentCurrency = sanitizeCurrencyCode(rawConfig.paymentCurrency || rawConfig.currency);
  const paymentProvider = sanitizePublicPaymentProvider(
    rawConfig.paymentProvider || rawConfig.provider?.provider || rawConfig.checkout?.provider,
    'manual'
  );
  const paymentRequired = Boolean(rawConfig.paymentRequired) && paymentProvider !== 'manual';
  const primaryProvider = normalizeProviderOption(
    rawConfig.provider || { provider: paymentProvider },
    paymentCurrency
  );
  const providers = Array.isArray(rawConfig.providers)
    ? rawConfig.providers
      .map((provider) => normalizeProviderOption(provider, paymentCurrency))
      .filter(Boolean)
    : [];
  const checkout = normalizeCheckoutConfig(rawConfig.checkout, {
    flow,
    paymentProvider,
    paymentRequired,
    productSku,
  });
  const readiness = rawConfig.readiness && typeof rawConfig.readiness === 'object'
    ? rawConfig.readiness
    : {};

  return {
    success: rawConfig.success !== false,
    ownerUid: sanitizeText(rawConfig.ownerUid, 180),
    flow,
    webinarId: sanitizeText(rawConfig.webinarId || fallback.webinarId, 240),
    webinarTitle: sanitizeText(rawConfig.webinarTitle, 200),
    productSku,
    productTitle: sanitizeText(rawConfig.productTitle, 200),
    pricingAnchor: sanitizeText(rawConfig.pricingAnchor, 120),
    priceQualifier: sanitizeText(rawConfig.priceQualifier, 120),
    downloadOfferId: sanitizeText(rawConfig.downloadOfferId, 240),
    downloadOfferTitle: sanitizeText(rawConfig.downloadOfferTitle, 240),
    paymentRequired,
    paymentProvider,
    paymentAmount,
    paymentCurrency,
    amount: paymentAmount,
    currency: paymentCurrency,
    defaultProvider: sanitizePublicPaymentProvider(rawConfig.defaultProvider, 'manual'),
    providerSource: sanitizeText(rawConfig.providerSource, 80),
    provider: primaryProvider,
    providers,
    checkout,
    readiness: {
      ready: Boolean(readiness.ready),
      ownerActionRequired: Boolean(readiness.ownerActionRequired),
      unavailableReason: sanitizeText(readiness.unavailableReason, 80),
    },
    agreement: rawConfig.agreement && typeof rawConfig.agreement === 'object'
      ? {
        required: rawConfig.agreement.required !== false,
        version: sanitizeText(rawConfig.agreement.version, 120),
        title: sanitizeText(rawConfig.agreement.title, 240),
        acknowledgment: sanitizeText(rawConfig.agreement.acknowledgment, 1200),
        url: sanitizeText(rawConfig.agreement.url, 2048),
      }
      : undefined,
    settingsUpdatedAt: sanitizeText(rawConfig.settingsUpdatedAt, 80),
    traceId: sanitizeText(rawConfig.traceId, 120),
  };
}

function getPublicPaymentConfigCacheKey({ flow, webinarId = '', productSku = '' }) {
  const owner = getOwnerPayload();
  return [
    flow,
    sanitizeText(webinarId, 240),
    sanitizeText(productSku, 40),
    sanitizeText(owner.workspaceOwnerUid || owner.ownerUid, 180),
  ].join(':');
}

function pruneMapToLimit(map, maxEntries, { now = Date.now(), pruneExpired = false } = {}) {
  if (pruneExpired) {
    map.forEach((entry, key) => {
      if (entry?.expiresAt && entry.expiresAt <= now) {
        map.delete(key);
      }
    });
  }

  while (map.size >= maxEntries) {
    const oldestKey = map.keys().next().value;
    if (oldestKey === undefined) {
      break;
    }
    map.delete(oldestKey);
  }
}

function getPublicPaymentConfigCache(cacheKey, now = Date.now()) {
  const cached = publicPaymentConfigCache.get(cacheKey);
  if (!cached) {
    return null;
  }
  if (!cached.expiresAt || cached.expiresAt <= now) {
    publicPaymentConfigCache.delete(cacheKey);
    return null;
  }
  return cached.value;
}

function setPublicPaymentConfigCache(cacheKey, config) {
  if (PUBLIC_PAYMENT_CONFIG_CACHE_TTL_MS <= 0) {
    return;
  }
  pruneMapToLimit(publicPaymentConfigCache, PUBLIC_PAYMENT_CONFIG_CACHE_MAX_ENTRIES, {
    now: Date.now(),
    pruneExpired: true,
  });
  publicPaymentConfigCache.set(cacheKey, {
    value: config,
    expiresAt: Date.now() + PUBLIC_PAYMENT_CONFIG_CACHE_TTL_MS,
  });
}

function trackPublicPaymentConfigInflight(cacheKey, request) {
  pruneMapToLimit(publicPaymentConfigInflight, PUBLIC_PAYMENT_CONFIG_CACHE_MAX_ENTRIES);
  publicPaymentConfigInflight.set(cacheKey, request);
  return request;
}

function getPublicPaymentStatusInflightKey(flow, values = {}) {
  const owner = getOwnerPayload();
  return [
    flow,
    sanitizeText(values.bookingId, 240),
    sanitizeText(values.registrationId, 240),
    sanitizeText(values.webinarId, 240),
    sanitizeText(values.checkoutSessionId, 240),
    sanitizeText(owner.workspaceOwnerUid || owner.ownerUid, 180),
  ].join(':');
}

function trackPublicPaymentStatusInflight(cacheKey, request) {
  pruneMapToLimit(publicPaymentStatusInflight, PUBLIC_PAYMENT_STATUS_INFLIGHT_MAX_ENTRIES);
  publicPaymentStatusInflight.set(cacheKey, request);
  return request;
}

async function requestPublicPaymentConfig() {
  return unavailableTmsOperation();
}

export async function loadPublicPaymentConfig(options = {}) {
  const flow = sanitizePublicPaymentFlow(options.flow);
  if (!flow) {
    throw new Error('Payment flow must be consultation, course, or paperwork package.');
  }

  const webinarId = flow === 'webinar'
    ? sanitizeText(options.webinarId || options.webinar || options.sourceId, 240)
    : '';
  const productSku = flow === 'paperwork_package'
    ? sanitizePaperworkPackageSku(options.productSku || options.packageSku || options.sku)
    : '';
  if (flow === 'paperwork_package' && !productSku) {
    throw new Error('Paperwork package SKU must be basic, standard, or premium.');
  }
  const cacheKey = getPublicPaymentConfigCacheKey({ flow, webinarId, productSku });
  const now = Date.now();
  const cached = getPublicPaymentConfigCache(cacheKey, now);
  if (!options.forceRefresh && cached) {
    return cached;
  }

  if (!options.forceRefresh && publicPaymentConfigInflight.has(cacheKey)) {
    return publicPaymentConfigInflight.get(cacheKey);
  }

  const request = requestPublicPaymentConfig({ flow, webinarId, productSku }).then((config) => {
    setPublicPaymentConfigCache(cacheKey, config);
    return config;
  }).finally(() => {
    publicPaymentConfigInflight.delete(cacheKey);
  });

  return trackPublicPaymentConfigInflight(cacheKey, request);
}

export async function loadPaperworkPackagePaymentConfig(productSku, options = {}) {
  return loadPublicPaymentConfig({
    ...options,
    flow: 'paperwork_package',
    productSku,
  });
}

export function resolvePublicCheckoutPath(config = {}, provider = '', key = '', fallback = '') {
  const expectedProvider = sanitizePublicPaymentProvider(provider, '');
  const checkout = config?.checkout && typeof config.checkout === 'object' ? config.checkout : {};
  if (expectedProvider && sanitizePublicPaymentProvider(checkout.provider, '') !== expectedProvider) {
    return sanitizePublicApiPath(fallback, fallback);
  }
  return sanitizePublicApiPath(checkout[key], fallback);
}

export async function loadConsultationPaymentStatus() {
  return unavailableTmsOperation();
}

export async function loadWebinarPaymentStatus() {
  return unavailableTmsOperation();
}

export function getPublicPaymentConfigRuntimeConfig() {
  return {
    apiBaseUrl: API_BASE_URL,
    ownerUid: DEFAULT_OWNER_UID,
    workspaceOwnerUid: DEFAULT_WORKSPACE_OWNER_UID,
    paymentConfigPath: sanitizePublicApiPath(PUBLIC_PAYMENT_CONFIG_PATH, '/api/public/payments/config'),
    cacheTtlMs: PUBLIC_PAYMENT_CONFIG_CACHE_TTL_MS,
    cacheMaxEntries: PUBLIC_PAYMENT_CONFIG_CACHE_MAX_ENTRIES,
    statusInflightMaxEntries: PUBLIC_PAYMENT_STATUS_INFLIGHT_MAX_ENTRIES,
  };
}
